import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { communityApi } from '@/api/client';

/****************
 * BLOCKED USERS (App Store guideline 1.2). The members you blocked in the
 * Community, newest first, each with Unblock. Lives in the Profile pop-up,
 * the web's settings; the phone shows the same list in Settings.
 ****************/
export function BlockedUsers({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation('community');
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['community', 'blocked'], queryFn: communityApi.blocked, enabled });
  const unblock = useMutation({
    mutationFn: (handle: string) => communityApi.unblock(handle),
    onSuccess: (r) => {
      toast.success(t('blocked.unblocked', { handle: r.handle }));
      void qc.invalidateQueries({ queryKey: ['community'] });
    },
    onError: () => toast.error(t('blocked.unblockFailed')),
  });
  const rows = list.data?.blocked ?? [];
  return (
    <>
      <p className="text-[12px] text-muted-foreground">{t('blocked.intro')}</p>
      {list.isLoading && <p className="text-[12px] text-muted-foreground">{t('view.loading')}</p>}
      {list.isError && <p className="text-[12px] text-danger">{t('blocked.loadFailed')}</p>}
      {list.data && rows.length === 0 && <p className="text-[12px] text-muted-foreground">{t('blocked.empty')}</p>}
      <div className="divide-y divide-border/40">
        {rows.map((b) => (
          <div key={b.handle} className="flex items-center gap-3 py-2 text-[13px]">
            <span className="flex-1 truncate font-medium">@{b.handle}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={unblock.isPending}
              onClick={() => {
                if (window.confirm(t('blocked.unblockConfirm', { handle: b.handle }))) unblock.mutate(b.handle);
              }}
            >
              {t('blocked.unblock')}
            </Button>
          </div>
        ))}
      </div>
    </>
  );
}
